import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';
import { useAppSelector } from '@app/hooks/reduxHooks';
import { hasRole } from '@app/utils/role.utils';
import { UserRole } from '@app/utils/role.utils';
import * as S from './SiderMenu.styles';
import { sidebarNavigation, SidebarNavigationItem } from '../sidebarNavigation';

interface SiderContentProps {
  setCollapsed: (isCollapsed: boolean) => void;
}

const sidebarNavFlat = sidebarNavigation.reduce(
  (result: SidebarNavigationItem[], current) =>
    result.concat(current.children && current.children.length > 0 ? current.children : current),
  [],
);

const SiderMenu: React.FC<SiderContentProps> = ({ setCollapsed }) => {
  const { t } = useTranslation();
  const location = useLocation();
  const user = useAppSelector((state) => state.user.user);
  const userRole = user?.role as UserRole | undefined;

  // Filter navigation items based on user role
  const filteredNavigation = useMemo(() => {
    if (!userRole) return [];

    return sidebarNavigation
      .filter((nav) => {
        // If item has allowedRoles, check if user has permission
        if (nav.allowedRoles) {
          return hasRole(userRole, nav.allowedRoles);
        }
        // If no allowedRoles specified, allow all authenticated users
        return true;
      })
      .map((nav) => {
        // Filter children if they exist
        if (nav.children) {
          const filteredChildren = nav.children.filter((child) => {
            if (child.allowedRoles) {
              return hasRole(userRole, child.allowedRoles);
            }
            return true;
          });
          return { ...nav, children: filteredChildren };
        }
        return nav;
      })
      .filter((nav) => {
        // Remove parent items if they have no visible children
        if (nav.children && nav.children.length === 0) {
          return false;
        }
        return true;
      });
  }, [userRole]);

  const sidebarNavFlat = filteredNavigation.reduce(
    (result: SidebarNavigationItem[], current) =>
      result.concat(current.children && current.children.length > 0 ? current.children : current),
    [],
  );

  const currentMenuItem = sidebarNavFlat.find(({ url }) => url === location.pathname);
  const defaultSelectedKeys = currentMenuItem ? [currentMenuItem.key] : [];

  const openedSubmenu = filteredNavigation.find(({ children }) =>
    children?.some(({ url }) => url === location.pathname),
  );
  const defaultOpenKeys = openedSubmenu ? [openedSubmenu.key] : [];

  return (
    <S.Menu
      mode="inline"
      defaultSelectedKeys={defaultSelectedKeys}
      defaultOpenKeys={defaultOpenKeys}
      onClick={() => setCollapsed(true)}
      items={filteredNavigation.map((nav) => {
        const isSubMenu = nav.children?.length;

        return {
          key: nav.key,
          title: t(nav.title),
          label: isSubMenu ? t(nav.title) : <Link to={nav.url || ''}>{t(nav.title)}</Link>,
          icon: nav.icon,
          children:
            isSubMenu &&
            nav.children &&
            nav.children.map((childNav) => ({
              key: childNav.key,
              label: <Link to={childNav.url || ''}>{t(childNav.title)}</Link>,
              title: t(childNav.title),
            })),
        };
      })}
    />
  );
};

export default SiderMenu;
